/**
 * New Home Page - Dashboard with Greeting, Search, and App Cards
 *
 * Features:
 * - Personalized greeting with user's first name
 * - Semantic search / prompt bar
 * - App cards for navigating to different sections
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useAuth as useAuthStore } from '../../stores/useAuth';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  PageContent,
  SectionHeader,
} from '../../components/ui';
import {
  ArrowUpIcon,
  SparklesIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  StarIcon as StarOutlineIcon,
  BeakerIcon,
  CpuChipIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  ScaleIcon,
  PresentationChartBarIcon,
  CircleStackIcon,
  ClockIcon,
  InboxStackIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useFavorites } from '../../stores/useFavorites';
import { isAppletEnabled, isBaseChatPlatformEnabled, isFrontendAppEnabled } from '../../shared/lib/applets';
import { canAccessPulseAdmin } from '../../shared/lib/pulse-access';

// App card configuration - Only actual "apps", not admin tools
// Each app has permissions that grant access (user needs ANY of them)
interface AppCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  iconKey: string; // For storing in favorites
  path: string;
  color: string;
  bgGradient: string;
  features?: string[];
  requiredPermissions: string[];
}

export const appCards: AppCard[] = [
  {
    id: 'pulse',
    title: 'PULSE',
    description: 'Create workshop surveys, capture team readiness, and prepare facilitators with high-signal context.',
    icon: SignalIcon,
    iconKey: 'chart-bar',
    path: '/pulse',
    color: 'text-rose-700',
    bgGradient: 'from-rose-500/10 to-amber-500/5',
    features: ['Workshop Setup', 'Readiness Survey', 'Facilitator Brief'],
    requiredPermissions: ['pulse:access', 'platform:admin'],
  },
  {
    id: 'talent',
    title: 'AI Recruiter',
    description: 'Search for candidates, analyze talent pools, and generate personalized outreach.',
    icon: UserGroupIcon,
    iconKey: 'user-group',
    path: '/talent/analysis-config',
    color: 'text-rose-600',
    bgGradient: 'from-rose-500/10 to-rose-600/5',
    features: ['Talent Search', 'Candidate Scoring', 'Email Generation'],
    requiredPermissions: ['recruiter:config:read', 'recruiter:results:read', 'platform:admin'],
  },
  {
    id: 'chat',
    title: 'Chat',
    description: 'Chat with your data and documents using AI-powered assistants.',
    icon: ChatBubbleLeftRightIcon,
    iconKey: 'chat',
    path: '/chat',
    color: 'text-blue-600',
    bgGradient: 'from-blue-500/10 to-blue-600/5',
    features: ['Document Q&A', 'Data Analysis', 'Source Citations'],
    requiredPermissions: ['assistant:access', 'platform:admin'],
  },
  {
    id: 'workspaces',
    title: 'Workspaces',
    description: 'Create knowledge workspaces and upload documents for RAG-powered chat.',
    icon: DocumentTextIcon,
    iconKey: 'folder',
    path: '/workspaces',
    color: 'text-violet-600',
    bgGradient: 'from-violet-500/10 to-violet-600/5',
    features: ['Document Upload', 'Knowledge Bases', 'Workspace Management'],
    // TODO: Revisit granular permissions for workspaces (workspaces:read, workspaces:write)
    requiredPermissions: ['assistant:access', 'platform:admin'],
  },
  {
    id: 'catalog',
    title: 'Catalog',
    description: 'Inspect warehouse catalog coverage, provider provenance, and steward correction requests.',
    icon: CircleStackIcon,
    iconKey: 'circle-stack',
    path: '/catalog',
    color: 'text-sky-700',
    bgGradient: 'from-sky-500/10 to-emerald-600/5',
    features: ['Asset Browser', 'Provider Provenance', 'Corrections'],
    requiredPermissions: ['connections:read', 'catalog:admin', 'platform:admin'],
  },
  {
    id: 'prd-generator',
    title: 'PRD Refiner',
    description: 'Import a draft, close material gaps through focused questions, and produce an implementation-ready PRD.',
    icon: SparklesIcon,
    iconKey: 'sparkles',
    path: '/prd-generator',
    color: 'text-amber-600',
    bgGradient: 'from-amber-500/10 to-orange-600/5',
    features: ['Draft Import', 'Gap Review', 'Version Compare'],
    requiredPermissions: ['prd_generator:read', 'prd_generator:write', 'platform:admin'],
  },
  {
    id: 'microsite-generator',
    title: 'Microsite Generator',
    description: 'Generate self-contained HTML microsites from a text description or upload your own.',
    icon: PresentationChartBarIcon,
    iconKey: 'presentation-chart-bar',
    path: '/microsite-generator',
    color: 'text-teal-600',
    bgGradient: 'from-teal-500/10 to-cyan-600/5',
    features: ['AI-Generated HTML', 'File Upload', 'Live Preview'],
    requiredPermissions: ['microsite_generator:read', 'platform:admin'],
  },
  {
    id: 'cashflow',
    title: '13-Week Cash Flow',
    description: 'Forecast liquidity, manage covenant compliance, and model cash scenarios.',
    icon: BanknotesIcon,
    iconKey: 'banknotes',
    path: '/cashflow',
    color: 'text-teal-600',
    bgGradient: 'from-teal-500/10 to-teal-600/5',
    features: ['Cash Forecasting', 'Scenario Analysis', 'Covenant Monitoring'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'cashflow_v2',
    title: '13-Week Cash Flow V2',
    description: 'SEER Liquidity OS workflows for liquidity pulse, forecast intelligence, portfolio, and working capital.',
    icon: BanknotesIcon,
    iconKey: 'banknotes',
    path: '/cashflow-v2',
    color: 'text-cyan-600',
    bgGradient: 'from-cyan-500/10 to-teal-600/5',
    features: ['Liquidity Pulse', 'Forecast Grid', 'WC Recommendations'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'close_calendar',
    title: 'Close Calendar',
    description: 'Orchestrate month-end close tasks, dependency gates, approvals, and audit evidence.',
    icon: ClockIcon,
    iconKey: 'clock',
    path: '/close-calendar',
    color: 'text-indigo-600',
    bgGradient: 'from-indigo-500/10 to-slate-600/5',
    features: ['Close Tasks', 'Rule Gates', 'Audit Trail'],
    requiredPermissions: ['close_calendar:read', 'platform:admin'],
  },
  {
    id: 'payroll_entry',
    title: 'Payroll Accrual',
    description: 'Calculate payroll accruals, tie out headcount, and approve NetSuite journal entries.',
    icon: DocumentCheckIcon,
    iconKey: 'document-check',
    path: '/payroll-entry',
    color: 'text-sky-600',
    bgGradient: 'from-sky-500/10 to-cyan-600/5',
    features: ['Headcount Tie-Out', 'NetSuite JE', 'SOX Approval'],
    requiredPermissions: ['payroll:read', 'platform:admin'],
  },
  {
    id: 'ap_cutoff_enforcer',
    title: 'AP Cut-Off Enforcer',
    description: 'Detect late AP invoices, enforce cut-off policy, and generate accrual entries.',
    icon: ClipboardDocumentListIcon,
    iconKey: 'clipboard-document-list',
    path: '/ap-cutoff-enforcer',
    color: 'text-indigo-600',
    bgGradient: 'from-indigo-500/10 to-sky-600/5',
    features: ['Late Invoices', 'Receiving Tie-Out', 'AP Accrual'],
    requiredPermissions: ['ap_cutoff:read', 'platform:admin'],
  },
  {
    id: 'ap_invoice_agent',
    title: 'AP Invoice Agent',
    description: 'Extract AP inbox invoices, stage bill records, and review Redshift invoice history.',
    icon: InboxStackIcon,
    iconKey: 'inbox-stack',
    path: '/ap-invoice-agent',
    color: 'text-cyan-700',
    bgGradient: 'from-cyan-500/10 to-emerald-600/5',
    features: ['Inbox Extraction', 'Bill Staging', 'Invoice History'],
    requiredPermissions: ['ap_invoice:read', 'platform:admin'],
  },
  {
    id: 'judgmental_accruals',
    title: 'Judgmental Accruals',
    description: 'Review ASC 450 contingencies, approval gates, and NetSuite-ready judgment accrual entries.',
    icon: ScaleIcon,
    iconKey: 'scale',
    path: '/judgmental-accruals',
    color: 'text-slate-700',
    bgGradient: 'from-slate-500/10 to-rose-600/5',
    features: ['ASC 450 Review', 'Approval Gates', 'JE Export'],
    requiredPermissions: ['judgmental_accruals:read', 'platform:admin'],
  },
  {
    id: 'lag_accruals',
    title: 'Lag Accruals',
    description: 'Review variable lag accruals, controller decisions, and NetSuite close files.',
    icon: ClipboardDocumentListIcon,
    iconKey: 'clipboard-document-list',
    path: '/lag-accruals',
    color: 'text-slate-700',
    bgGradient: 'from-slate-500/10 to-zinc-600/5',
    features: ['HITL Review', 'JE Export', 'Close Runbook'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'variable_lag_accruals',
    title: 'Variable Lag Accruals',
    description: 'Review EC-02d accrual exceptions, capture controller HITL actions, and export NetSuite-ready journals.',
    icon: ClipboardDocumentListIcon,
    iconKey: 'document-check',
    path: '/variable-lag-accruals',
    color: 'text-slate-700',
    bgGradient: 'from-slate-500/10 to-red-600/5',
    features: ['HITL Review', 'JE Export', 'Source Evidence'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'asc_606_revenue_waterfall',
    title: 'ASC 606 Revenue Waterfall',
    description: 'Run revenue recognition close checks across contracts, obligations, deferred revenue, and posting exports.',
    icon: BanknotesIcon,
    iconKey: 'banknotes',
    path: '/asc-606-revenue-waterfall',
    color: 'text-emerald-700',
    bgGradient: 'from-emerald-500/10 to-sky-600/5',
    features: ['ASC 606 Review', 'Deferred Revenue', 'JE Export'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'deferred_revenue_scheduler',
    title: 'Deferred Revenue Scheduler',
    description: 'Build the deferred revenue roll-forward, split ST/LT balances, route D-code review, and export NetSuite reclasses.',
    icon: DocumentCheckIcon,
    iconKey: 'document-check',
    path: '/deferred-revenue-scheduler',
    color: 'text-teal-700',
    bgGradient: 'from-teal-500/10 to-sky-600/5',
    features: ['ST/LT Split', 'Gate 2 Review', 'JE Export'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'subledger_revenue_reconciler',
    title: 'Sub-Ledger Revenue Reconciler',
    description: 'Tie out revenue subledger postings to GL credits, route reconciling items, and export balanced correction journals.',
    icon: BanknotesIcon,
    iconKey: 'banknotes',
    path: '/subledger-revenue-reconciler',
    color: 'text-sky-700',
    bgGradient: 'from-sky-500/10 to-emerald-600/5',
    features: ['GL Tie-Out', 'Controller Review', 'JE Export'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'cloud_accruals',
    title: 'Cloud Accruals',
    description: 'Accrue cloud provider usage from Redshift evidence with FinOps review and Langfuse-traced controls.',
    icon: CircleStackIcon,
    iconKey: 'circle-stack',
    path: '/cloud-accruals',
    color: 'text-cyan-700',
    bgGradient: 'from-cyan-500/10 to-blue-600/5',
    features: ['Redshift Evidence', 'FinOps Review', 'JE Export'],
    requiredPermissions: ['cashflow:read', 'platform:admin'],
  },
  {
    id: 'contract_automation',
    title: 'Contract Automation',
    description: 'Reconcile HubSpot deal context, Drive SOW extraction, Slack approvals, and revenue timing review.',
    icon: DocumentCheckIcon,
    iconKey: 'document-check',
    path: '/contract-automation',
    color: 'text-emerald-700',
    bgGradient: 'from-emerald-500/10 to-cyan-600/5',
    features: ['SOW Extraction', 'HubSpot Reconciliation', 'Slack Review'],
    requiredPermissions: ['contract_automation:read', 'platform:admin'],
  },
  {
    id: 'adoption',
    title: 'Adoption Analytics',
    description: 'Track platform usage, user engagement, and feature adoption.',
    icon: ChartBarIcon,
    iconKey: 'chart-bar',
    path: '/adoption',
    color: 'text-emerald-600',
    bgGradient: 'from-emerald-500/10 to-emerald-600/5',
    features: ['Usage Metrics', 'Engagement Trends', 'Feature Adoption'],
    requiredPermissions: ['adoption:view_dashboard', 'platform:admin'],
  },
];

// Get time-based greeting
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// Get first name from user
function getFirstName(user: { first_name?: string; full_name?: string } | null | undefined): string {
  if (user?.first_name) return user.first_name;
  if (user?.full_name) {
    const parts = user.full_name.trim().split(' ');
    return parts[0] || 'there';
  }
  return 'there';
}

export default function NewHomePage() {
  const { user } = useAuth();
  const { hasAnyPermission } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const { toggleFavorite, isFavorite } = useFavorites();
  const isRagEnabled = isBaseChatPlatformEnabled();
  const isAdoptionEnabled = isAppletEnabled('adoption');
  const isTalentEnabled = isAppletEnabled('talent');

  // Memoize filtered apps based on user permissions
  const accessibleApps = useMemo(() => {
    if (!user) return [];
    return appCards.filter(
      app => (app.requiredPermissions.length === 0 || hasAnyPermission(app.requiredPermissions))
        && isFrontendAppEnabled(app.id)
        && (app.id !== 'pulse' || canAccessPulseAdmin(user))
    );
  }, [user, hasAnyPermission]);

  // Check if user can see Labs section
  const canSeeAgentConfig = hasAnyPermission(['labs:agents:access', 'platform:admin']);
  const canSeeResumeParsing = hasAnyPermission(['labs:resume_parsing:access', 'platform:admin']);
  const canSeeLabs = canSeeAgentConfig || canSeeResumeParsing;

  const greeting = getGreeting();
  const firstName = getFirstName(user);

  // Handle hash navigation for scrolling to apps section
  useEffect(() => {
    if (location.hash === '#apps') {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        const appsSection = document.getElementById('apps-section');
        if (appsSection) {
          appsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In modular mode, only route to pages that are enabled.
      if (isRagEnabled) {
        navigate(`/chat?q=${encodeURIComponent(searchQuery)}`);
      } else if (isAdoptionEnabled) {
        navigate('/adoption');
      }
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'job-spec':
        navigate('/talent/analysis-config');
        break;
      case 'talent-search':
        navigate('/talent/outreach');
        break;
      case 'analytics':
        navigate('/adoption');
        break;
    }
  };

  return (
    <Layout>
      <div className="h-full overflow-y-auto bg-gray-50 dark:bg-dark-bg" data-toolbar-scroll-container>
        {/* Hero Section with Gradient + Greeting */}
        <div className="relative">
          {/* Hero content with gradient background */}
          <div className="bg-gradient-to-b from-eliza-red/5 to-transparent dark:from-eliza-red/10 dark:to-transparent">
            <div className="max-w-[1600px] mx-auto px-8 pt-20 pb-8 md:pt-24">
              <div className="text-center max-w-2xl mx-auto">
                {/* Greeting - using DS title font */}
                <h1 className="font-title text-4xl md:text-5xl text-charcoal dark:text-white mb-8">
                  {greeting}, {firstName}.
                </h1>

                {/* Search / Prompt Bar */}
                <form onSubmit={handleSearch} className="relative mb-6">
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ask a question or describe what you need..."
                    className="h-14 text-base rounded-2xl shadow-card border-gray-200 dark:border-gray-700 pr-14 pl-5"
                  />
                  <Button
                    type="submit"
                    variant="brand"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 shadow-lg"
                  >
                    <ArrowUpIcon className="h-5 w-5" />
                  </Button>
                </form>

                {/* Quick Actions */}
                <div className="flex flex-wrap justify-center gap-3">
                  {isTalentEnabled && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('job-spec')}
                      >
                        <SparklesIcon className="h-4 w-4 text-eliza-red" />
                        Create job spec
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleQuickAction('talent-search')}
                      >
                        <UserGroupIcon className="h-4 w-4 text-eliza-red" />
                        Search talent pool
                      </Button>
                    </>
                  )}
                  {isAdoptionEnabled && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleQuickAction('analytics')}
                    >
                      <ChartBarIcon className="h-4 w-4 text-eliza-red" />
                      Pipeline analytics
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* App Cards Section */}
        <PageContent id="apps-section">
          <SectionHeader
            title="Your Apps"
            description="Quick access to your most-used applications"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {accessibleApps.map((app) => {
              const IconComponent = app.icon;
              const favorited = isFavorite(app.id);

              const handleToggleFavorite = (e: React.MouseEvent) => {
                e.stopPropagation(); // Prevent card navigation
                toggleFavorite({
                  id: app.id,
                  name: app.title,
                  path: app.path,
                  icon: app.iconKey,
                });
              };

              return (
                <Card
                  key={app.id}
                  className="group cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 overflow-hidden relative"
                  onClick={() => navigate(app.path)}
                >
                  {/* Favorite star button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleToggleFavorite}
                    className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-white/80 dark:bg-dark-surface/80 hover:bg-white dark:hover:bg-dark-surface shadow-sm hover:scale-110 transition-all duration-150"
                    title={favorited ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {favorited ? (
                      <StarSolidIcon className="h-4 w-4 text-amber-500" />
                    ) : (
                      <StarOutlineIcon className="h-4 w-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                    )}
                  </Button>

                  {/* Gradient header */}
                  <div className={`h-20 bg-gradient-to-br ${app.bgGradient} flex items-center justify-center`}>
                    <div className={`p-3 rounded-xl bg-white/80 dark:bg-dark-surface/80 shadow-sm ${app.color}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                  </div>

                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-subtitle text-charcoal dark:text-white group-hover:text-eliza-red transition-colors">
                        {app.title}
                      </h3>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                      {app.description}
                    </p>

                    {app.features && (
                      <div className="flex flex-wrap gap-1.5">
                        {app.features.map((feature) => (
                          <Badge key={feature} variant="secondary" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

        </PageContent>

        {/* Labs Section - Only show if user has labs permissions */}
        {canSeeLabs && (
          <PageContent className="pt-0">
            <SectionHeader
              title="Labs"
              description="Experimental features and developer tools"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {/* Agent Configuration */}
              {canSeeAgentConfig && (
                <Card
                  className="group cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 overflow-hidden"
                  onClick={() => navigate('/admin/agents')}
                >
                  <div className="h-20 bg-gradient-to-br from-purple-500/10 to-purple-600/5 flex items-center justify-center">
                    <div className="p-3 rounded-xl bg-white/80 dark:bg-dark-surface/80 shadow-sm text-purple-600">
                      <CpuChipIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-subtitle text-charcoal dark:text-white group-hover:text-eliza-red transition-colors">
                        Agent Configuration
                      </h3>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                      Configure AI agents, models, and provider settings.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="info" className="text-xs">
                        Experimental
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Resume Parsing Test */}
              {canSeeResumeParsing && (
                <Card
                  className="group cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 overflow-hidden"
                  onClick={() => navigate('/admin/resume-parsing-test')}
                >
                  <div className="h-20 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 flex items-center justify-center">
                    <div className="p-3 rounded-xl bg-white/80 dark:bg-dark-surface/80 shadow-sm text-indigo-600">
                      <BeakerIcon className="h-6 w-6" />
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-subtitle text-charcoal dark:text-white group-hover:text-eliza-red transition-colors">
                        Resume Parsing Test
                      </h3>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                      Test and debug resume parsing capabilities.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="info" className="text-xs">
                        Developer Tool
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </PageContent>
        )}
      </div>
    </Layout>
  );
}
